/**
 * DOMINIOS DE INVENTARIO (F13.7 — arquitectura de navegación)
 *
 * REGLA: «El menú representa dominios funcionales, no funcionalidades individuales.» Una consulta, un proceso o
 * una acción nueva NO crea una entrada lateral: va DENTRO de su dominio, como pestaña, vista o acción contextual.
 *
 *   Artículos    → Artículos · Configuración (familias, unidades…) · Escaneos pendientes   + acción «Importar»
 *   Almacenes    → Almacenes · Almacenes móviles (furgonetas)
 *   Movimientos  → Movimientos · Stock (hoy o a una fecha) · Kárdex · Cierres            + «Exportar» contextual
 *   Inventarios  → recuentos físicos (proceso propio: recuento → diferencias → validación → regularización)
 *
 * Una sola fuente: el menú lateral (`NAV`) y las pestañas salen de aquí. Cada pestaña lleva su permiso: se
 * ocultan las que no se tienen (la API sigue siendo la autoridad; agrupar una ruta no da acceso a nada).
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useSesion } from '../contexto.tsx';

export interface PestanaDominio {
  readonly texto: string;
  readonly a: string;
  /** Basta con uno. */
  readonly permisos: readonly string[];
  /** Rutas que pertenecen a esta pestaña (además de `a`), para marcarla activa. */
  readonly rutas?: readonly string[];
}
export type Dominio = 'articulos' | 'almacenes' | 'movimientos';

export const DOMINIOS: Readonly<Record<Dominio, { readonly titulo: string; readonly pestanas: readonly PestanaDominio[] }>> = {
  articulos: {
    titulo: 'Artículos',
    pestanas: [
      { texto: 'Artículos', a: '/inventario/articulos', permisos: ['articulos:leer'],
        rutas: ['/inventario/importaciones', '/inventario/lotes', '/inventario/series'] },
      { texto: 'Configuración', a: '/inventario/maestros', permisos: ['maestros_inventario:gestionar'] },
      { texto: 'Escaneos pendientes', a: '/inventario/escaneos-pendientes', permisos: ['escaneos:resolver'] },
    ],
  },
  almacenes: {
    titulo: 'Almacenes',
    pestanas: [
      { texto: 'Almacenes', a: '/inventario/almacenes', permisos: ['almacenes:leer'] },
      { texto: 'Almacenes móviles', a: '/inventario/almacenes-moviles', permisos: ['almacenes_moviles:leer'] },
    ],
  },
  movimientos: {
    titulo: 'Movimientos',
    pestanas: [
      { texto: 'Movimientos', a: '/inventario/movimientos', permisos: ['movimientos_stock:leer'],
        rutas: ['/inventario/operaciones', '/inventario/exportaciones'] },
      { texto: 'Stock', a: '/inventario/stock', permisos: ['stock:leer', 'inventario_historico:leer'], rutas: ['/inventario/stock-historico'] },
      { texto: 'Kárdex', a: '/inventario/kardex', permisos: ['inventario_historico:leer'] },
      { texto: 'Cierres', a: '/inventario/cierres', permisos: ['cierres_inventario:leer'] },
    ],
  },
};

const enRuta = (a: string, ruta: string): boolean => ruta === a || ruta.startsWith(`${a}/`);
export const pestanaActiva = (p: PestanaDominio, ruta: string): boolean => [p.a, ...(p.rutas ?? [])].some((a) => enRuta(a, ruta));

/** Pestañas del dominio que el usuario puede abrir. */
export function pestanasVisibles(d: Dominio, puede: (p: string) => boolean): readonly PestanaDominio[] {
  return DOMINIOS[d].pestanas.filter((p) => p.permisos.some(puede));
}

/** Pestañas de un dominio, encima de la cabecera de cada pantalla suya. Una sola visible: no se pinta. */
export function PestanasDominio({ dominio }: { dominio: Dominio }): ReactNode {
  const { puede } = useSesion();
  const ruta = useLocation().pathname;
  const visibles = pestanasVisibles(dominio, puede);
  if (visibles.length < 2) return null;
  return (
    <nav className="tabs tabs-dominio" aria-label={`Secciones de ${DOMINIOS[dominio].titulo}`}>
      {visibles.map((p) => {
        const activa = pestanaActiva(p, ruta);
        return (
          <Link key={p.a} to={p.a} className={activa ? 'activo' : ''} aria-current={activa ? 'page' : undefined}>{p.texto}</Link>
        );
      })}
    </nav>
  );
}
